import { Util } from '@antv/g6';
import { Position } from 'ui/workflows/flowchart/constants';
import { DrawOpts, NodeModel, Shape } from '../../../../types';
import { shapeNames, groupIds } from '../../../../shapes/constants';
import {
  adjustAttrsPosition,
  drawText,
  getAllOffsets,
  getNodeTitle,
  getPositionRelativeToShape,
  updateText,
} from '../../../../shapes/utils';
import { getDescBottomShape } from './helpers';
import { NODE_SECTIONS } from '../base.constants';

export const getInfoX = (options: DrawOpts['options']) => {
  const [, , , wrapperPaddingLeft] = Util.formatPadding(options.padding);
  const iconBackground = options[shapeNames.iconBackground];
  const [, iconPaddingRight, , iconPaddingLeft] = getAllOffsets(iconBackground);
  return wrapperPaddingLeft + iconBackground.width + iconPaddingRight + iconPaddingLeft;
};

const getInfoWidth = ({ options, size }: Pick<DrawOpts, 'options' | 'size'>) => {
  const [width = 0] = size || [];
  const [, paddingRight] = Util.formatPadding(options?.padding);
  return width - getInfoX(options) - paddingRight;
};

const getTextSegments = ({ nodeData = {} }: NodeModel) => nodeData?.textSegments ?? {};

export const getTextData = (name = '', model: NodeModel) => {
  const { nodeData = {} } = model;
  return {
    text: nodeData[name] || '',
    segmentConfig: getTextSegments(model)?.[name],
  };
};

/* -------------------------------------------------------------------------- */
/* Title                                                                      */
/* -------------------------------------------------------------------------- */
export const drawTitle = ({ options, group, model, x, y }: DrawOpts) => {
  return group.addShape('text', {
    name: shapeNames.title,
    className: shapeNames.title,
    draggable: true,
    attrs: adjustAttrsPosition(
      {
        text: getNodeTitle(model as NodeModel, options),
        x: getInfoX(options),
        ...options[shapeNames.title],
      },
      { x, y },
    ),
  });
};

/* -------------------------------------------------------------------------- */
/* Desc Bottom                                                                */
/* -------------------------------------------------------------------------- */

const MAX_DESC_BOTTOM_ROWS = 2;

interface DestBottomDrawOptions extends DrawOpts {
  /** The desc bottom group  */
  dbGroup?: DrawOpts['group'];
  /** The shape to measure the vertical distance from */
  yRelativeTo?: Shape | string;
}

const getDescBottomDrawArgs = ({
  dbGroup,
  group,
  model,
  options,
  size,
  yRelativeTo = shapeNames.title,
  x = 0,
}: DestBottomDrawOptions) => {
  const infoX = getInfoX(options);
  const style = options[shapeNames.descBottom];
  const { text = '', segmentConfig } = getTextData(NODE_SECTIONS.DESC_BOTTOM, model as NodeModel);
  const maxWidth = getInfoWidth({ options, size });
  const startY = getPositionRelativeToShape({
    relativeTo: yRelativeTo,
    group,
    style,
    position: Position.Bottom,
  });

  return {
    className: shapeNames.descBottom,
    group: dbGroup,
    maxRows: MAX_DESC_BOTTOM_ROWS,
    maxWidth,
    segmentConfig,
    startX: infoX + x,
    startY,
    text,
    style,
  };
};

export const drawDescBottom = ({
  group,
  model,
  options,
  size,
  yRelativeTo,
  x,
}: DestBottomDrawOptions) => {
  const dbGroup = group.addGroup({ id: groupIds.descBottom });

  drawText(getDescBottomDrawArgs({ options, group, model, size, yRelativeTo, dbGroup, x }));

  return dbGroup;
};

export const updateDescBottom = ({ options, group, model, size }: DrawOpts) => {
  const dbGroup = getDescBottomShape(group);

  if (dbGroup) {
    updateText(getDescBottomDrawArgs({ options, group, model, size, dbGroup }));
  } else {
    drawDescBottom({ options, group, model, size });
  }
};
